import { Box, Typography } from '@mui/material';
import React, { useContext, useState } from 'react';
import MuiDialogWrapper from '../../components/MuiDialogWrapper/MuiDialogWrapper';
import { i18n } from '../../i18n/i18n';
import ProfileInfo from '../../components/ProfileInfo/ProfileInfo';
import ImageDetails from '../../components/ImageDetails/ImageDetails';
import { ReactComponent as LockIcon } from '../../images/lock.svg';
import { ReactComponent as PublicIcon } from '../../images/public.svg';
import {
    getAccountActions,
    getAccountsStatusMessage,
    getConnectionSections,
    getKeywordsFromWatchLists,
} from '../../utils/utils';
import { GlobalContext } from '../../contexts/globalContext';
import { Connection } from '../../models/common-types';
import { COLORS, Tooltip } from '@cellebrite/saas-ui-common';
import Popup from '../../components/Popup/Popup';
import IconButton from '@mui/material/IconButton';
import { SECTION } from '../../constants/constants';
import { AddTagIcon } from '@cellebrite/saas-ui-icons';
import TagsManager from '../../components/TagsManager/TagsManager';
import TagsViewer from '../../components/TagsViewer/TagsViewer';
import MarkedText from '../../components/MarkedText/MarkedText';
import MultiMarkedText from '../../components/MultiMarkedText/MultiMarkedText';
import ViewWatchLists from '../../components/ViewWatchLists/ViewWatchLists';

interface CustomDialogProps {
    close: () => void;
    data: {
        account: Connection;
        styles: object;
        section: string;
        onClose?: any;
    };
}

const ConnectionInformationDialog: React.FC<CustomDialogProps> = ({ close, data }) => {
    const { collectionData, adminData, reportData } = useContext(GlobalContext);
    const [accountStateMessages] = useState(getAccountsStatusMessage(data.account.public, data.account.site));
    const [tags, setTags] = useState(data.account.tags || []);
    const connectionKeywords = getKeywordsFromWatchLists(
        data.account.watchlist,
        reportData.currentHighlightsWatchlist?.id,
    );
    return (
        <MuiDialogWrapper
            prefixCloseWindow={[
                getAccountActions(
                    data.account,
                    collectionData.createCollection.mutate,
                    null,
                    null,
                    null,
                    adminData.isQuotaLimitReached,
                    reportData.collection?.taggable ? (
                        <Popup
                            actionElement={(handleOpen, anchorRef, open) => {
                                return (
                                    <Box ref={anchorRef} sx={{ ml: 0.5 }}>
                                        <IconButton
                                            sx={{
                                                'backgroundColor': open ? COLORS.MUTED_DOUBLE_HOVER : null,
                                                '&:hover': { backgroundColor: COLORS.MUTED_DOUBLE_HOVER },
                                            }}
                                            disableRipple
                                            onClick={handleOpen}
                                        >
                                            <AddTagIcon fill={open ? COLORS.BRAND : COLORS.MUTED_DARK} />
                                        </IconButton>
                                    </Box>
                                );
                            }}
                            contentElement={(handleClose) => (
                                <Box>
                                    <TagsManager
                                        setTags={setTags}
                                        checkedTags={tags}
                                        section={SECTION.CONNECTIONS}
                                        elementId={data.account.identifier}
                                    />
                                </Box>
                            )}
                        />
                    ) : null,
                ),
            ]}
            styles={data.styles}
            title={
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
                        {reportData.collection?.taggable && <TagsViewer checkedTags={tags} />}
                        {!!data.account.watchlist?.length && <ViewWatchLists watchLists={data.account.watchlist} />}
                        <Typography variant="body2" sx={{ fontWeight: '700' }}>
                            {i18n.translate('Connection Information')}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: '18px' }}>
                        <ProfileInfo imageSrc={data.account.localProfilePicture} platform={data.account.site} />
                        {reportData.displayHighlights ? (
                            <MultiMarkedText
                                containerProps={{
                                    className: 'color-mh',
                                    sx: {
                                        'ml': 1,
                                        'fontWeight': '500',
                                        'fontSize': '16px',
                                        '& mark': {
                                            backgroundColor: `${COLORS.NEGATIVE_SOFT} !important`,
                                        },
                                    },
                                }}
                                searchTextArr={connectionKeywords}
                            >
                                {data.account.name}
                            </MultiMarkedText>
                        ) : (
                            <MarkedText
                                containerProps={{
                                    className: 'color-mh',
                                    sx: {
                                        'ml': 1,
                                        'fontWeight': '500',
                                        'fontSize': '16px',
                                        '& mark': { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` },
                                    },
                                }}
                                searchText={reportData.find}
                            >
                                {data.account.name}
                            </MarkedText>
                        )}
                        {typeof data.account.public === 'boolean' && (
                            <Box>
                                {data.account.public ? (
                                    <Tooltip
                                        placement="right"
                                        content={
                                            <Box>
                                                <Typography sx={{ fontWeight: 'bold' }}>
                                                    {accountStateMessages.title}
                                                </Typography>
                                                {accountStateMessages.explanationText && (
                                                    <Typography>{accountStateMessages.explanationText}</Typography>
                                                )}
                                            </Box>
                                        }
                                    >
                                        <PublicIcon className="fill-md" style={{ marginLeft: '13px' }} />
                                    </Tooltip>
                                ) : (
                                    <Tooltip
                                        placement="right"
                                        content={
                                            <Box>
                                                <Typography sx={{ fontWeight: 'bold' }}>
                                                    {accountStateMessages.title}
                                                </Typography>
                                                {accountStateMessages.explanationText && (
                                                    <Typography>{accountStateMessages.explanationText}</Typography>
                                                )}
                                            </Box>
                                        }
                                    >
                                        <LockIcon className="fill-md" style={{ marginLeft: '13px' }} />
                                    </Tooltip>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            }
            onClose={() => {
                data.onClose?.(tags);
                close();
            }}
            content={
                <ImageDetails
                    sections={getConnectionSections(
                        data.account,
                        tags,
                        reportData.find,
                        reportData.displayHighlights,
                        connectionKeywords,
                    )}
                    sources={[
                        data.account.coverImageUrl || data.account.localProfilePicture,
                        data.account.coverImageLocalUrl,
                    ]}
                />
            }
        />
    );
};

export default ConnectionInformationDialog;
